import type { QuoteGenerationRecovery } from "@workspace/api-client-react";
import { isQuoteNetworkFailure } from "./quote-response-recovery";

export interface PendingQuoteGeneration {
  requestId: string;
  startedAt: number;
  generationLogId?: string;
  delivery?: "delivered";
  formValues: { description?: string; customerName?: string; projectAddress?: string };
  reviewSettings?: {
    includeVat: boolean;
    contingencyPercent: string; overheadPercent: string; profitPercent: string;
    contingencyEnabled: boolean; overheadEnabled: boolean; profitEnabled: boolean;
  };
}
const key = (owner: string) => `proper-tool:pending-quote:${owner}`;
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function loadPendingGeneration(owner: string, store: Storage = localStorage): PendingQuoteGeneration | null {
  const raw = store.getItem(key(owner));
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    if (uuid.test(value.requestId) && Number.isFinite(value.startedAt) && value.formValues && typeof value.formValues === "object")
      return value;
  } catch { /* Corrupt browser data is not an instruction to send a new POST. */ }
  throw Object.assign(new Error("The saved generation reference could not be read. Please contact support before generating again."),
    { code: "RECOVERY_UNCONFIRMED" });
}

export function rememberGeneration(owner: string, pending: PendingQuoteGeneration, store: Storage = localStorage): void {
  try {
    store.setItem(key(owner), JSON.stringify(pending));
  } catch {
    throw Object.assign(new Error("Your browser could not save recovery details. No quotation was started. Please allow site storage and try again."),
      { code: "GENERATION_NOT_STARTED", generationTerminal: true });
  }
}

export function forgetGeneration(owner: string, requestId: string, store: Storage = localStorage): void {
  // A late completion must never erase a newer request or another user's marker.
  if (loadPendingGeneration(owner, store)?.requestId === requestId) store.removeItem(key(owner));
}

/** Completed references remain recoverable, but must not block a new project.
 * generationLogId also recognises completed references saved by older clients.
 */
export function isCompletedGeneration(pending: PendingQuoteGeneration): boolean {
  return pending.delivery === "delivered" || !!pending.generationLogId;
}

export function beginGeneration(owner: string, pending: PendingQuoteGeneration, store: Storage = localStorage): void {
  const existing = loadPendingGeneration(owner, store);
  if (existing && !isCompletedGeneration(existing)) {
    throw Object.assign(new Error("An earlier quotation still needs checking. Recover it before starting another."),
      { code: "RECOVERY_UNCONFIRMED" });
  }
  rememberGeneration(owner, pending, store);
}

export function markGenerationDelivered(owner: string, requestId: string, store: Storage = localStorage): void {
  const pending = loadPendingGeneration(owner, store);
  // A late result cannot mark the next project's request as completed.
  if (pending?.requestId === requestId) rememberGeneration(owner, { ...pending, delivery: "delivered" }, store);
}

export function isCurrentGeneration(requestId: string | undefined, owner: string | undefined,
  activeRequestId: string | null, activeOwner: string | undefined): boolean {
  return !!requestId && !!owner && requestId === activeRequestId && owner === activeOwner;
}

export type GenerationConnection = "running" | "checking" | "reconnecting";
export const RECOVERY_CHECK_WINDOW_MS = 10 * 60 * 1000;

function waitForCheck(signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("pageshow", wake);
      window.removeEventListener("online", wake);
      window.removeEventListener("focus", wake);
      signal.removeEventListener("abort", abort);
    };
    const wake = () => { if (!document.hidden) { cleanup(); resolve(); } };
    const abort = () => { cleanup(); reject(new DOMException("Aborted", "AbortError")); };
    // No polling is required while hidden; returning immediately wakes this wait.
    const timer = document.hidden ? undefined : setTimeout(wake, 3000);
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("pageshow", wake);
    window.addEventListener("online", wake);
    window.addEventListener("focus", wake);
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) abort();
  });
}

async function checkWithResume(
  lookup: (signal: AbortSignal) => Promise<QuoteGenerationRecovery>, parent: AbortSignal,
): Promise<QuoteGenerationRecovery> {
  const check = new AbortController();
  const abort = () => check.abort();
  const resume = () => { if (!document.hidden) check.abort(); };
  parent.addEventListener("abort", abort, { once: true });
  document.addEventListener("visibilitychange", resume);
  window.addEventListener("pageshow", resume);
  window.addEventListener("online", resume);
  const timeout = setTimeout(abort, 15000);
  if (parent.aborted) check.abort();
  try {
    return await lookup(check.signal);
  } catch (error) {
    if (check.signal.aborted && !parent.aborted) throw new TypeError("Failed to fetch generation status");
    throw error;
  } finally {
    clearTimeout(timeout);
    parent.removeEventListener("abort", abort);
    document.removeEventListener("visibilitychange", resume);
    window.removeEventListener("pageshow", resume);
    window.removeEventListener("online", resume);
  }
}

export async function runBackgroundQuote<T>({
  start, lookup, signal, onConnection,
}: {
  start?: (signal: AbortSignal) => Promise<T>;
  lookup: (signal: AbortSignal) => Promise<QuoteGenerationRecovery>;
  signal: AbortSignal;
  onConnection: (state: GenerationConnection) => void;
}): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener("abort", abort, { once: true });
  if (signal.aborted) controller.abort();
  const startedAt = Date.now();
  let submissionDisconnected = !start;
  const never = new Promise<T>(() => {});
  const submitted = start ? start(controller.signal).catch(error => {
    if (!controller.signal.aborted && (isQuoteNetworkFailure(error) || error?.code === "QUOTE_GENERATION_EXISTS")) {
      submissionDisconnected = true;
      onConnection("reconnecting");
      return never;
    }
    throw error;
  }) : never;
  const poll = async (): Promise<T> => {
    while (!controller.signal.aborted) {
      if (document.hidden) await waitForCheck(controller.signal);
      if (controller.signal.aborted) break;
      try {
        // Always check a saved result BEFORE applying a foreground time limit.
        // A sleeping phone can return well after the countdown has expired.
        const response = await checkWithResume(lookup, controller.signal);
        if (response.status === "completed") {
          if (!Array.isArray(response.result?.lineItems)) throw Object.assign(
            new Error("The saved quotation could not be read. Please contact support."), { code: "RECOVERY_UNCONFIRMED" });
          return response.result as T;
        }
        if (response.status === "failed") throw Object.assign(new Error(response.message || "The quotation could not be generated."),
          { code: "GENERATION_FAILED", generationTerminal: true });
        if (response.status === "unconfirmed") throw Object.assign(new Error(response.message), { code: "RECOVERY_UNCONFIRMED" });
        if (!["pending", "running"].includes(response.status)) throw Object.assign(
          new Error("The generation status could not be read. Its reference is still saved."), { code: "RECOVERY_UNCONFIRMED" });
        if (response.status === "pending" && submissionDisconnected && Date.now() - startedAt > 30000) {
          throw Object.assign(new Error("The server has not confirmed receiving this request. Its reference is saved; check again before starting a new quotation."),
            { code: "RECOVERY_UNCONFIRMED" });
        }
        onConnection(response.status === "running" ? "running" : "checking");
      } catch (error) {
        if (!isQuoteNetworkFailure(error)) throw error;
        onConnection("reconnecting");
      }
      if (Date.now() - startedAt >= RECOVERY_CHECK_WINDOW_MS) {
        throw Object.assign(new Error("We could not confirm this quotation yet. Its recovery reference is saved. Check again rather than generating another quote."),
          { code: "RECOVERY_UNCONFIRMED" });
      }
      await waitForCheck(controller.signal);
    }
    throw new DOMException("Aborted", "AbortError");
  };
  try {
    return await Promise.race([submitted, poll()]);
  } finally {
    controller.abort();
    signal.removeEventListener("abort", abort);
  }
}
